/**
 * Field ordering for the Form Fields sidebar AND for the PDF the author ships.
 *
 * TWO FACTS, AND THEY ARE NOT THE SAME FACT. Conflating them was a WCAG 2.1
 * SC 2.4.3 defect in this file, so the separation is stated before anything
 * else and the types keep it apart:
 *
 *   1. HOW THE AUTHOR BROWSES THE LIST — `FieldSortPrefs`. A view. Sorting the
 *      sidebar by name to find a field is a search, not an editorial decision,
 *      and it changes NOTHING about any artefact. These prefs are remembered in
 *      localStorage because a view preference is personal and durable.
 *   2. WHAT ORDER THE DOCUMENT GETS — `ReadingOrderMode`. AUTO means the
 *      generator sorts page-major by geometry; MANUAL means the field array is
 *      the reading order and nothing re-sorts it. This is a property OF THE
 *      DOCUMENT, it is carried in `FormConfiguration.readingOrderMode`, and it
 *      is deliberately NOT stored in this module's localStorage keys — see
 *      saveFieldSortPrefs().
 *
 * What went wrong, recorded so it is not re-derived: `isManualOrder` used to
 * read `prefs.sortKey !== 'reading-order'`, so picking "Name A → Z" in order to
 * FIND a field sent readingOrderMode MANUAL, and the array was emitted in the
 * order the sidebar was displaying it. The alphabetical order became the PDF's
 * reading and tab order, and the sort persisted in localStorage, so it followed
 * the author to every later document. Both functions are gone;
 * `orderForGeneration` is what emission must call now, and it takes the MODE,
 * not the view.
 *
 * One measured correction to how that defect has been described elsewhere, so
 * nobody plans against the wrong symptom. It does NOT tab 1, 10, 11, 12, 2 on
 * numbered field names: `fieldSortName`'s comparison passes `numeric: true`, so
 * `row2col1` really does collate before `row10col1`, and a row-major numbered
 * grid happened to come out in reading order by coincidence. The failure
 * appears wherever names do not track geometry, which is the normal case -
 * semantic names (a `zipCode` at the top of the page sorts last), names applied
 * column-major, or simply "Name Z → A". The WCAG 2.1 SC 2.4.3 failure is real;
 * the numeric-collation example of it is not.
 *
 * WHAT IS STILL TRUE, AND IS THE POINT OF D37: the author can take the reading
 * order over, because geometry cannot tell you whether a two-column form reads
 * across or down. They do it by an act of ORDERING — choosing manual mode, or
 * moving a row — never by sorting the list. Both acts are explicit, and the
 * sidebar names the mode in force so neither can be silent.
 *
 * "Manual" order is the selectedFields array order itself: fields append on
 * draw, drag-to-reorder rewrites the array, and save/load round-trips preserve
 * it (via the session's field_configuration_ids), so no timestamp and no
 * per-field ordinal is needed. SEQUENCE IS POSITION IN THE ARRAY AND NOTHING
 * ELSE - FormField.displayOrder was deleted on both sides for exactly that
 * reason (D37).
 *
 * READING ORDER mirrors backend pdfua/ReadingOrder.java: page, then down the
 * page, then across, with rows BANDED so a two-column row whose tops differ by
 * 3pt stays one row. The mirror is what lets the sidebar show the order the PDF
 * will actually tab in - change one, change both, the same contract
 * fieldFonts.autoFontSize has with calculateFontSize.
 */

export type FieldSortKey = 'reading-order' | 'creation-asc' | 'creation-desc' | 'name-asc' | 'name-desc';

/** What the backend is told to do with the field array it receives. */
export type ReadingOrderMode = 'AUTO' | 'MANUAL';

export interface FieldSortPrefs {
  sortKey: FieldSortKey;
  groupByPage: boolean;
}

export const FIELD_SORT_STORAGE_KEY = 'pfbFieldSort';
export const FIELD_GROUP_BY_PAGE_STORAGE_KEY = 'pfbFieldGroupByPage';

/** Reading order by default: an author who never opens this control gets a form
 *  that tabs the way it reads, which is the whole point of D37. */
export const DEFAULT_FIELD_SORT_PREFS: FieldSortPrefs = {
  sortKey: 'reading-order',
  groupByPage: false,
};

/** Geometry decides unless the author says otherwise. An author who never finds
 *  the control still ships a form that tabs the way it reads. */
export const DEFAULT_READING_ORDER_MODE: ReadingOrderMode = 'AUTO';

/**
 * The falsy-safe read of a STORED session's mode (editing_sessions
 * .reading_order_mode, riding the session payload since October 2026 — before
 * that the mode had no column, which is why Manual used to revert to AUTO on
 * every reload).
 *
 * Only the literal 'MANUAL' means Manual. A legacy session has no key at all, a
 * fresh backend answers 'AUTO' explicitly, and anything else — null, '', junk a
 * proxy mangled — must land AUTO rather than be trusted into the type, because
 * MANUAL is the value with authority: it tells generation to publish the array
 * as the document's reading and tab order. Guessing Manual from bad data is
 * publishing an order nobody chose, which is D37 itself.
 */
export function readingOrderModeFromSession(stored: unknown): ReadingOrderMode {
  return stored === 'MANUAL' ? 'MANUAL' : DEFAULT_READING_ORDER_MODE;
}

/** The view that shows exactly what the document will get. Reading-order view
 *  mirrors the AUTO sort; array order IS the MANUAL sequence. groupByPage is
 *  only headings over reading order, so it survives there; over array order it
 *  re-sorts by page and therefore must come off. */
const READING_ORDER_VIEW: FieldSortKey = 'reading-order';
const ARRAY_ORDER_VIEW: FieldSortKey = 'creation-asc';

const VALID_SORT_KEYS: FieldSortKey[] = ['reading-order', 'creation-asc', 'creation-desc', 'name-asc', 'name-desc'];

/**
 * The field array to SEND, in the order the document will use it.
 *
 * Takes the mode, never the view: this is the one function emission calls, and
 * the signature is what stops a browsing preference reaching a PDF again.
 *
 * AUTO sorts here as well as in the backend on purpose. PDFGenerationService
 * runs ReadingOrder.pageMajor over whatever it is sent, but the generated
 * project bakes the array as given and carries no comparator (D51), so sorting
 * before the payload is built is what keeps the two artefacts we promise are
 * identical identical. MANUAL copies rather than returns the input, because a
 * caller that then maps over the result should not be handed React state.
 */
export function orderForGeneration<T extends SortableField>(fields: T[], mode: ReadingOrderMode): T[] {
  if (mode === 'MANUAL') return [...fields];
  return sortFields(fields, { sortKey: READING_ORDER_VIEW, groupByPage: false });
}

/**
 * Is the list the author is looking at the sequence the document will get?
 *
 * The honest answer has to be computable, because the sidebar states it and
 * because reordering is only offered while it is true — dragging row 3 below
 * row 4 in a list sorted by name cannot mean anything about the PDF.
 */
export function listShowsGeneratedOrder(prefs: FieldSortPrefs, mode: ReadingOrderMode): boolean {
  return mode === 'MANUAL'
    ? prefs.sortKey === ARRAY_ORDER_VIEW && !prefs.groupByPage
    : prefs.sortKey === READING_ORDER_VIEW;
}

/** The view prefs that make listShowsGeneratedOrder true for this mode, keeping
 *  the author's grouping choice wherever it is compatible with it. */
export function viewShowingGeneratedOrder(mode: ReadingOrderMode, prefs: FieldSortPrefs): FieldSortPrefs {
  return mode === 'MANUAL'
    ? { sortKey: ARRAY_ORDER_VIEW, groupByPage: false }
    : { sortKey: READING_ORDER_VIEW, groupByPage: prefs.groupByPage };
}

export function getFieldSortPrefs(): FieldSortPrefs {
  try {
    const rawKey = localStorage.getItem(FIELD_SORT_STORAGE_KEY);
    const sortKey = VALID_SORT_KEYS.includes(rawKey as FieldSortKey)
      ? (rawKey as FieldSortKey)
      : DEFAULT_FIELD_SORT_PREFS.sortKey;
    const groupByPage = localStorage.getItem(FIELD_GROUP_BY_PAGE_STORAGE_KEY) === 'true';
    return { sortKey, groupByPage };
  } catch {
    return DEFAULT_FIELD_SORT_PREFS;
  }
}

/**
 * The VIEW is remembered. THE MODE IS NOT, and that omission is the fix.
 *
 * A reading order belongs to one document — it is an editorial decision about
 * one layout — so remembering "manual" here would reintroduce the defect in its
 * worst form: the next document the author opened would silently publish its
 * raw drawing order as its tab order, which is D37 itself. The mode lives in
 * PDFUpload's document state and resets with the document.
 */
export function saveFieldSortPrefs(prefs: FieldSortPrefs): void {
  try {
    localStorage.setItem(FIELD_SORT_STORAGE_KEY, prefs.sortKey);
    localStorage.setItem(FIELD_GROUP_BY_PAGE_STORAGE_KEY, String(prefs.groupByPage));
  } catch {
    // Storage unavailable (private mode quota etc.) — sort still works for the session.
  }
}

interface SortableField {
  id: string;
  name?: string;
  pageNumber?: number;
  /** Top-down canvas coordinates. Absent on a field that was never drawn. */
  x?: number;
  y?: number;
  height?: number;
}

/** Mirrors ReadingOrder.MAX_ROW_TOLERANCE / MIN_ROW_TOLERANCE (points). */
const MAX_ROW_TOLERANCE = 12;
const MIN_ROW_TOLERANCE = 6;

/** Half the median field height, clamped - mirrors ReadingOrder.rowTolerance. */
function rowTolerance(fields: SortableField[]): number {
  const heights = fields
    .map(field => field.height ?? 0)
    .filter(height => height > 0)
    .sort((a, b) => a - b);
  if (heights.length === 0) return MAX_ROW_TOLERANCE;
  const mid = Math.floor(heights.length / 2);
  const median = heights.length % 2 === 1 ? heights[mid] : (heights[mid - 1] + heights[mid]) / 2;
  return Math.max(MIN_ROW_TOLERANCE, Math.min(MAX_ROW_TOLERANCE, median / 2));
}

interface IndexedField<T extends SortableField> {
  field: T;
  creationIndex: number;
}

/**
 * Page, then down the page, then across it - the mirror of backend
 * pdfua/ReadingOrder.pageMajor.
 *
 * Canvas y is TOP-DOWN, so ascending y IS downward; the PDF flips it on write.
 * Bands are assigned in a pass BEFORE the comparator runs, because "within
 * tolerance" is not transitive and a pairwise comparator built on it mis-sorts.
 * A band is anchored on its first member, not its previous one, or a staircase
 * of near-misses swallows a whole page into one row.
 */
function readingOrder<T extends SortableField>(indexed: IndexedField<T>[]): IndexedField<T>[] {
  const centre = (field: SortableField) => (field.y ?? 0) + (field.height ?? 0) / 2;

  const byPage = new Map<number, IndexedField<T>[]>();
  indexed.forEach(entry => {
    const page = entry.field.pageNumber ?? 1;
    const bucket = byPage.get(page);
    if (bucket) {
      bucket.push(entry);
    } else {
      byPage.set(page, [entry]);
    }
  });

  const out: IndexedField<T>[] = [];
  Array.from(byPage.keys()).sort((a, b) => a - b).forEach(page => {
    const pageEntries = byPage.get(page) || [];
    const tolerance = rowTolerance(pageEntries.map(entry => entry.field));

    const provisional = [...pageEntries].sort((a, b) =>
      (centre(a.field) - centre(b.field))
      || ((a.field.x ?? 0) - (b.field.x ?? 0))
      || (a.creationIndex - b.creationIndex));

    const band = new Map<string, number>();
    let bandNumber = 0;
    let anchor = provisional.length > 0 ? centre(provisional[0].field) : 0;
    provisional.forEach(entry => {
      if (centre(entry.field) - anchor > tolerance) {
        bandNumber += 1;
        anchor = centre(entry.field);
      }
      band.set(entry.field.id, bandNumber);
    });

    provisional.sort((a, b) =>
      ((band.get(a.field.id) ?? 0) - (band.get(b.field.id) ?? 0))
      || ((a.field.x ?? 0) - (b.field.x ?? 0))
      || (a.creationIndex - b.creationIndex));
    out.push(...provisional);
  });
  return out;
}

/** Name used for alphabetical comparison — mirrors the sidebar's display name. */
export function fieldSortName(field: SortableField): string {
  if (field.name && field.name.trim() !== '' && field.name !== 'loading...') {
    return field.name;
  }
  return field.id;
}

/**
 * Returns a NEW array ordered per prefs; never mutates the input. Grouping by
 * page sorts by pageNumber first, then applies the sort key within each page.
 * Sorting is stable: creation (array) order breaks all ties.
 */
export function sortFields<T extends SortableField>(fields: T[], prefs: FieldSortPrefs): T[] {
  const indexed = fields.map((field, creationIndex) => ({ field, creationIndex }));

  // Reading order is page-major already, so groupByPage only adds the headings.
  if (prefs.sortKey === 'reading-order') {
    return readingOrder(indexed).map(entry => entry.field);
  }

  indexed.sort((a, b) => {
    if (prefs.groupByPage) {
      const pageDiff = (a.field.pageNumber ?? 1) - (b.field.pageNumber ?? 1);
      if (pageDiff !== 0) return pageDiff;
    }
    switch (prefs.sortKey) {
      case 'creation-desc':
        return b.creationIndex - a.creationIndex;
      case 'name-asc': {
        const cmp = fieldSortName(a.field).localeCompare(fieldSortName(b.field), undefined, { sensitivity: 'base', numeric: true });
        return cmp !== 0 ? cmp : a.creationIndex - b.creationIndex;
      }
      case 'name-desc': {
        const cmp = fieldSortName(b.field).localeCompare(fieldSortName(a.field), undefined, { sensitivity: 'base', numeric: true });
        return cmp !== 0 ? cmp : a.creationIndex - b.creationIndex;
      }
      case 'creation-asc':
      default:
        return a.creationIndex - b.creationIndex;
    }
  });

  return indexed.map(entry => entry.field);
}
